// ---------------------------------------------------------------------------
// Conta › Meu perfil — `PUT /users/me` (handoff 0097 §7.1).
//
// Autenticada, SEM gate de RBAC. Existe porque `PUT /users/:id` exige
// `iam:user:update` — permissão de administrar contas de terceiros (era
// `iam:user:create` até o RBAC por verbo do handoff 0110) —, então sem esta
// rota o usuário comum não conseguia editar o próprio cadastro.
//
// Só o NOME é editável, por decisão do backend: e-mail é chave de login
// (imutável no model), `role_id` seria escalada de privilégio e a senha tem
// rota própria (ver Account/ChangePassword). Não revoga sessão.
//
// Substitui a tela `pages/Register` em modo edição, que disparava `PUT /users`
// sem id — rota inexistente, 404 garantido. Aquela tela sequer estava no
// roteador.
// ---------------------------------------------------------------------------

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { updateMyProfile } from '../../../services/users/usersApi';
import * as actions from '../../../store/modules/auth/actions';
import { notifyApiError } from '../../../utils/feedback';

import FormField from '../../../components/Form/FormField';
import Loading from '../../../components/Loading';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import { Section, SectionTitle, SectionBody } from '../../../styles/layout/Section';
import { PrimaryButton } from '../../../styles/ui/Button';
import { TextLink } from '../../../styles/ui/Link';

import { FormStack, FormActions, ReadOnlyNote } from './styled';

const NAME_MIN = 3;
const NAME_MAX = 255;

export default function Profile() {
   const dispatch = useDispatch();
   const user = useSelector((state) => state.auth.user);

   const [nome, setNome] = useState(user?.nome || '');
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   const isDirty = nome.trim() !== (user?.nome || '').trim();

   const handleSubmit = async (event) => {
      event.preventDefault();
      if (isSubmitting) return;

      const trimmed = nome.trim();
      if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) {
         setFieldErrors({
            nome: `O nome precisa ter entre ${NAME_MIN} e ${NAME_MAX} caracteres.`,
         });
         return;
      }

      setFieldErrors({});
      setIsSubmitting(true);

      try {
         const updated = await updateMyProfile({ nome: trimmed });

         // Reflete no redux para a sidebar/menu mostrarem o nome novo sem
         // exigir novo login.
         dispatch(actions.authUserUpdated({ nome: updated?.nome ?? trimmed }));
         setNome(updated?.nome ?? trimmed);
         toast.success('Perfil atualizado com sucesso.');
      } catch (err) {
         if (err?.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
            setFieldErrors(err.fieldErrors);
         }
         notifyApiError(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Container>
         <Loading isLoading={isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Meu perfil</PageTitle>
               <PageSubtitle>
                  Dados da sua conta de acesso ao sistema.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionTitle>Dados pessoais</SectionTitle>

            <SectionBody>
               <FormStack onSubmit={handleSubmit} noValidate>
                  <FormField
                     label="Nome"
                     name="nome"
                     value={nome}
                     onChange={(e) => {
                        setNome(e.target.value);
                        setFieldErrors({});
                     }}
                     error={fieldErrors.nome}
                     disabled={isSubmitting}
                  />

                  <FormField
                     label="E-mail"
                     name="email"
                     type="email"
                     value={user?.email || ''}
                     onChange={() => {}}
                     disabled
                     hint="O e-mail é a chave de acesso e não pode ser alterado."
                  />

                  <FormActions>
                     {/* Escreve no banco → azul (FC7 §G.1). */}
                     <PrimaryButton
                        type="submit"
                        disabled={isSubmitting || !isDirty}
                     >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                     </PrimaryButton>
                  </FormActions>
               </FormStack>
            </SectionBody>
         </Section>

         <Section>
            <SectionTitle>Segurança</SectionTitle>

            <SectionBody>
               <ReadOnlyNote>
                  A troca de senha tem tela própria porque desconecta você de
                  todos os dispositivos.
               </ReadOnlyNote>
               <TextLink to="/account/password">Alterar minha senha</TextLink>
            </SectionBody>
         </Section>
      </Container>
   );
}
